// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Waft — the input layer (principle #23). One interface, three implementations, all
// producing the SAME per-zone motion energy the deterministic game consumes:
//   · camera — getUserMedia video → mirrored grayscale → frameDiffZones() (the sensor).
//   · manual — tap a zone (touch) or click / press 1–9 (desktop). The fallback, and a
//              complete whack-a-mole game in its own right.
//   · synthetic — a settable feed, for the browser test hook only.
// The sensor is an INPUT, never a gate: a missing/denied camera lands the player in the
// tap game with an honest message. Permission is asked from a tap. NOTHING is recorded,
// stored, or sent — frames are diffed in-place and discarded, and only the derived
// energy number is ever used; every track is stopped at teardown.

import { CAMERA_ALPHA } from './palette';
import { frameDiffZones, smoothEnergy } from './motion';
import { boardGeom, zoneAt, zoneCount, type Grid } from './layout';

export type SourceKind = 'camera' | 'manual' | 'synthetic';

export interface SwatSource {
  readonly kind: SourceKind;
  /** Current per-zone motion energy, each 0..1, length = zones. Cheap; call each frame. */
  poll(): number[];
  /** Optional: paint the play backdrop (the camera draws its faint mirrored frame). */
  backdrop?(ctx: CanvasRenderingContext2D, w: number, h: number): void;
  dispose(): void;
}

/* ── camera ───────────────────────────────────────────────────────────────── */

const PROC_W = 64;
const PROC_H = 48;

export function cameraSupported(): boolean {
  return typeof navigator !== 'undefined' && !!navigator.mediaDevices && !!navigator.mediaDevices.getUserMedia;
}

/**
 * Request the camera. MUST be called from within a user gesture (iOS). Returns the
 * MediaStream, or null if unavailable / denied.
 */
export async function requestCamera(): Promise<MediaStream | null> {
  if (!cameraSupported()) return null;
  try {
    return await navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'user', width: { ideal: 320 }, height: { ideal: 240 } },
      audio: false,
    });
  } catch {
    return null;
  }
}

export interface CameraSource extends SwatSource {
  kind: 'camera';
  /** Resolves true once the video has real frame data (or false on timeout). */
  ready(timeoutMs: number): Promise<boolean>;
}

/**
 * @param ownsStream when false, dispose() does NOT stop the stream's tracks — the caller
 * (a session that reuses one camera across rounds) owns the stream's lifetime.
 */
export function createCameraSource(grid: Grid, stream: MediaStream, ownsStream = true): CameraSource {
  const zones = zoneCount(grid);
  const video = document.createElement('video');
  video.playsInline = true;
  video.muted = true;
  video.srcObject = stream;
  void video.play().catch(() => {
    /* autoplay may reject silently; readiness is polled below */
  });

  const proc = document.createElement('canvas');
  proc.width = PROC_W;
  proc.height = PROC_H;
  const pctx = proc.getContext('2d', { willReadFrequently: true });

  let prev: Uint8Array | null = null;
  let energy = new Array<number>(zones).fill(0);

  const grabGray = (): Uint8Array | null => {
    if (!pctx || video.readyState < 2 || video.videoWidth === 0) return null;
    // Mirror horizontally so the player's view matches the on-screen zones.
    pctx.save();
    pctx.setTransform(-1, 0, 0, 1, PROC_W, 0);
    pctx.drawImage(video, 0, 0, PROC_W, PROC_H);
    pctx.restore();
    const { data } = pctx.getImageData(0, 0, PROC_W, PROC_H);
    const gray = new Uint8Array(PROC_W * PROC_H);
    for (let i = 0, j = 0; i < data.length; i += 4, j++) {
      gray[j] = (data[i] * 77 + data[i + 1] * 150 + data[i + 2] * 29) >> 8;
    }
    return gray;
  };

  return {
    kind: 'camera',
    poll() {
      const cur = grabGray();
      if (cur) {
        if (prev) {
          const raw = frameDiffZones(prev, cur, PROC_W, PROC_H, grid.cols, grid.rows);
          energy = smoothEnergy(energy, raw);
        }
        prev = cur;
      } else {
        energy = smoothEnergy(energy, new Array<number>(zones).fill(0));
      }
      return energy.slice();
    },
    backdrop(ctx, w, h) {
      if (video.readyState < 2 || video.videoWidth === 0) return;
      // save/restore preserves the caller's transform (e.g. a shake offset); the
      // mirror composes on top of it.
      ctx.save();
      ctx.globalAlpha = CAMERA_ALPHA;
      ctx.translate(w, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(video, 0, 0, w, h);
      ctx.restore();
    },
    ready(timeoutMs) {
      const start = Date.now();
      return new Promise<boolean>((resolve) => {
        const check = (): void => {
          if (video.readyState >= 2 && video.videoWidth > 0) return resolve(true);
          if (Date.now() - start >= timeoutMs) return resolve(false);
          setTimeout(check, 60);
        };
        check();
      });
    },
    dispose() {
      if (ownsStream) {
        try {
          for (const t of stream.getTracks()) t.stop();
        } catch {
          /* stopping tracks is best-effort — the point is the camera light goes out */
        }
      }
      video.srcObject = null;
      video.remove();
    },
  };
}

/* ── manual: tap a zone (touch) / click / press 1–9 (desktop) ──────────────── */

export function createManualSource(boardEl: HTMLElement, grid: Grid): SwatSource {
  const zones = zoneCount(grid);
  const pending = new Array<boolean>(zones).fill(false);

  const onPointer = (e: PointerEvent): void => {
    const rect = boardEl.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    const { pad, gap } = boardGeom(rect.width, rect.height);
    const z = zoneAt(e.clientX - rect.left, e.clientY - rect.top, grid, rect.width, rect.height, pad, gap);
    if (z >= 0) pending[z] = true;
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key >= '1' && e.key <= '9') {
      const z = Number(e.key) - 1;
      if (z < zones) {
        pending[z] = true;
        e.preventDefault();
      }
    }
  };
  boardEl.addEventListener('pointerdown', onPointer);
  window.addEventListener('keydown', onKey);

  return {
    kind: 'manual',
    poll() {
      const out = pending.map((p) => (p ? 1 : 0));
      pending.fill(false);
      return out;
    },
    dispose() {
      boardEl.removeEventListener('pointerdown', onPointer);
      window.removeEventListener('keydown', onKey);
    },
  };
}

/* ── synthetic: a settable feed for the browser test hook ──────────────────── */

export interface SyntheticSource extends SwatSource {
  kind: 'synthetic';
  set(zone: number, energy: number): void;
  pulse(zone: number): void;
  clear(): void;
}

export function createSyntheticSource(grid: Grid): SyntheticSource {
  const zones = zoneCount(grid);
  const energy = new Array<number>(zones).fill(0);
  return {
    kind: 'synthetic',
    poll: () => energy.slice(),
    set(zone, e) {
      if (zone >= 0 && zone < zones) energy[zone] = e;
    },
    pulse(zone) {
      if (zone >= 0 && zone < zones) energy[zone] = 1;
    },
    clear() {
      energy.fill(0);
    },
    dispose() {},
  };
}
